import {all, call, fork, put, race, select, take} from "redux-saga/effects";
import {getType} from "typesafe-actions";
import {completeLevel} from "../../game/actions";
import {delay} from "redux-saga";
import {IApplicationState} from "../../index";
import {range} from "../../../shared/utils/range";
import {leaveQuestion} from "../../questions/actions";
import apiCall from "../../../shared/utils/apiCall";
import {trackError} from "../../log/actions";
import {parseTileId} from "../../../shared/utils/parseTileId";
import {ILevelMap, IQuestionAnswer} from "../../game/types";
import {measure} from "../../log/performance";
import {consume} from "../../payments/actions";
import updateLeaderboardEntry from "../../player/sagas/updateLeaderboardEntry";
import { changeProfile } from "../../player/actions";
import { gameSynchronized } from "../actions";

const REQUEST_TIMEOUT = 30000;

type AnswerHistoryItem = {
    levelId: number;
    questionId: number;
    answerId: number;
    answeredOn: string;
    isRight: boolean;
}

type SkippedQuestionHistoryItem = {
    levelId: number;
    id: number;
    actionOn: string;
    reason: "UsedHint" | "Timeout"
}

let previousSynchronizationData = '';

export function getSynchronizationData(state: Pick<IApplicationState, "settings" | "game" | "player" | "questions" | "hints" | "tutorial">) {
    const questionProgress = Object.values(state.questions.progress)
        .filter(answer => answer.levelId > state.player.levelsCompleted - 1 && answer.attempts < 0);

    const answerHistory: AnswerHistoryItem[] = [];
    const skippedQuestions: SkippedQuestionHistoryItem[] = [];

    for (const answer of questionProgress) {
        if (answer.isSkipped) {
            skippedQuestions.push({
                levelId: answer.levelId,
                id: answer.questionId,
                actionOn: new Date(answer.timestamp).toISOString(),
                reason: "UsedHint"
            });
            continue;
        }

        if (answer.isTimeoutExpired) {
            skippedQuestions.push({
                levelId: answer.levelId,
                id: answer.questionId,
                actionOn: new Date(answer.timestamp).toISOString(),
                reason: "Timeout"
            });
            continue;
        }

        answerHistory.push({
            levelId: answer.levelId,
            questionId: answer.questionId,
            answerId: answer.answerId,
            answeredOn: new Date(answer.timestamp).toISOString(),
            isRight: answer.isRight
        });
    }

    const levelMaps = Object.keys(state.game.levelMaps)
        .filter(levelId => Number(levelId) > state.player.levelsCompleted)
        .reduce((levelMaps, levelId: any) => ({
            ...levelMaps,
            [levelId]: state.game.levelMaps[levelId]
        }), {});

    const levelMapTileProgress = Object.keys(state.game.levelMapTileProgress)
        .filter(tileId => parseTileId(Number(tileId)).level > state.player.levelsCompleted)
        .reduce((tileProgress, tileId: any) => ({
            ...tileProgress,
            [tileId]: state.game.levelMapTileProgress[tileId]
        }), {});

    const questionIds = Object.values<ILevelMap>(levelMaps)
        .flatMap(levelMap => levelMap.tiles)
        .flatMap(tileColumns => tileColumns)
        .flatMap(tile => tile.questions || [])
        .concat(state.game.unusedQuestions);

    const questions = Object.keys(state.game.questions)
        .filter(questionId => questionIds.includes(Number(questionId)))
        .reduce((questions, questionId: any) => ({
            ...questions,
            [questionId]: state.game.questions[questionId]
        }), {});

    const answers = Object.values(state.game.answers)
        .filter(answer => questionIds.includes(answer.questionId))
        .reduce((answers, answer: IQuestionAnswer) => ({
            ...answers,
            [answer.id]: answer
        }), {});

    const authors = Object.values(state.game.questions)
        .filter(question => questionIds.includes(question.id) && question.author)
        .reduce((authors, question) => ({
            ...authors,
            [question.author!]: state.game.authors[question.author!]
        }), {});

    return {
        clientData: {
            ...state,
            game: {
                ...state.game,
                levelMaps,
                levelMapTileProgress,
                questions,
                answers,
                authors,
            }
        },
        progress: {
            answers: answerHistory,
            skippedQuestions: skippedQuestions,
        }
    };
}

function* synchronize() {
    try {
        yield fork(updateLeaderboardEntry);

        const state: Pick<IApplicationState, "settings" | "game" | "player" | "questions" | "hints" | "tutorial"> = yield select(({ settings, game, player, questions, hints, tutorial }: IApplicationState) => ({
            game,
            player,
            questions,
            hints: {
                appliedHints: hints.appliedHints
            },
            tutorial,
        }));

        const data = yield call(measure,
            "synchronize",
            "getSynchronizationData",
            () => JSON.stringify(getSynchronizationData(state)));

        if (data === previousSynchronizationData) {
            yield put(gameSynchronized());
            return;
        }

        yield call(apiCall, {
            params: {
                section: "progress",
                method: "post",
            },
            data: data,
            serializer: (data: any) => data,
            retryCount: 1,
            requestTimeout: REQUEST_TIMEOUT,
        });

        yield put(gameSynchronized());

        previousSynchronizationData = data;
    }
    catch (error) {
        yield put(trackError({
            category: "SynchronizeState",
            message: "Failed to post user progress",
            error: error,
        }));
    }
}

export default function* synchronizationWorker() {
    const {synchronizationTimeout} = yield select(({ settings }: IApplicationState) => ({
        synchronizationTimeout: settings.general.synchronizationTimeout || 0
    }));

    while (true) {
        yield race({
            completeLevel: take(getType(completeLevel)),
            questionAnswered10Times: all(range(10).map(_ => take(getType(leaveQuestion)))),
            purchaseProduct: take(getType(consume.success)),
            changeProfile: take(getType(changeProfile.success)),
            timeout: delay(Math.max(5000, synchronizationTimeout)),
        });

        yield fork(synchronize);
    }
}